<script setup>
import { computed, ref, watch } from "vue";
import { usePage } from "@inertiajs/vue3";

const page = usePage();

const show = ref(true);
const style = computed(
  () => page.props.jetstream?.flash?.bannerStyle || "success"
);
const message = computed(() => page.props.jetstream?.flash?.banner || "");

watch(message, async () => {
  show.value = true;
});
</script>

<template>
  <div>
    <div
      v-if="show && message"
      :class="{
        'bg-primary': style == 'success',
        'bg-danger': style == 'danger',
      }"
    >
      <div class="wrapper">
        <div>
          <div>
            <span
              :class="{
                'bg-primary lighten': style == 'success',
                'bg-danger lighten': style == 'danger',
              }"
            >
              <svg
                v-if="style == 'success'"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>

              <svg
                v-if="style == 'danger'"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </span>

            <p>
              {{ message }}
            </p>
          </div>

          <div>
            <button
              type="button"
              :class="{
                'success-states': style == 'success',
                'danger-states': style == 'danger',
              }"
              aria-label="Dismiss"
              @click.prevent="show = false"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  padding: 0.5rem 0.75rem;

  @media (min-width: $bp-sm) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  @media (min-width: $bp-lg) {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  & > div {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    & > div:first-of-type {
      width: 0;
      min-width: 0;

      align-items: center;
      display: flex;
      flex: 1 1 0%;

      & > span {
        @include rounded-borders-lg;

        display: flex;

        padding: 0.5rem;

        svg {
          --size: 1.25rem;
          height: var(--size);
          width: var(--size);

          color: var(--clr-text-light);
        }
      }

      & > p {
        @include ff-banner;

        margin-left: 0.75rem;
      }
    }

    & > div:last-of-type {
      flex-shrink: 0;

      @media (min-width: $bp-sm) {
        margin-left: 0.75rem;
      }

      button {
        @include rounded-borders-md;

        display: flex;

        margin-right: -0.25rem;
        padding: 0.5rem;

        transition-property: color, background-color, border-color,
          text-decoration-color, fill, stroke, opacity, box-shadow, transform,
          filter, backdrop-filter;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;

        @media (min-width: $bp-sm) {
          margin-right: -0.5rem;
        }

        &.success-states:hover,
        &.success-states:focus-visible {
          background-color: var(--clr-primary);
        }

        &.danger-states {
          background-color: var(--clr-danger);
        }
      }

      svg {
        --size: 1.25rem;
        width: var(--size);
        height: var(--size);

        color: var(--clr-text-light);
      }
    }
  }
}

/* background classes */
.bg-danger {
  background-color: var(--clr-danger);
}

.bg-primary {
  background-color: var(--clr-primary);
}

.lighten {
  filter: brightness(120%);
}
</style>
